import type * as React from "react";

import { cn } from "../cn";

// Design spec §8.2 CountPill: 18 px tall, min 18 wide, radius 6, 12 px / 500 tabular figures.
// Neutral is --muted / --muted-foreground, accent the --primary-soft / --primary-text pair,
// warning the warning soft / text pair. Counts above `max` read "99+". Counts only ever come
// from real data; a caller with no number renders nothing (honesty rule §1.11).
export type CountPillProps = Omit<React.ComponentProps<"span">, "children"> & {
  count: number;
  tone?: "neutral" | "accent" | "warning";
  max?: number;
};

const TONE: Record<NonNullable<CountPillProps["tone"]>, string> = {
  neutral: "bg-muted text-muted-foreground",
  accent: "bg-primary-soft text-primary-text",
  warning: "bg-warning-soft text-warning-text",
};

export function CountPill({
  count,
  tone = "neutral",
  max = 99,
  className,
  ...props
}: CountPillProps) {
  return (
    <span
      data-slot="count-pill"
      data-tone={tone}
      className={cn(
        "inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-sm px-1.5 text-xs leading-none font-medium tabular-nums",
        TONE[tone],
        className,
      )}
      {...props}
    >
      {count > max ? `${max}+` : count}
    </span>
  );
}
